import { Component, computed, inject, input, Input, LOCALE_ID, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProductVTO } from '../../../../pages/admin-dashboard/products/product.model';
import { ViewDetails } from '../../../../shared/ui/view-details/view-details';
import { productInfoMapper } from './product-info.mapper';
import { InfoField } from '../../../../shared/ui/view-details/view-details.model';
import { Zone } from './product-info.model';

@Component({
  selector: 'app-product-info',
  standalone: true,
  imports: [CommonModule, ViewDetails],
  templateUrl: './product-info.html',
  styleUrl: './product-info.scss',
})
export class ProductInfo {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  @Input() product: ProductVTO | null = null;
  productType = input<'has-sub' | 'has-no-sub' | 'sub-product'>();
  zones = input<Zone[]>([]);

  fields = computed<InfoField[]>(() => {
    const p = productInfoMapper(this.product!, this.isArabic(), this.zones());
    const ar = this.isArabic();
    const showPricing = this.productType() === 'has-no-sub' || this.productType() === 'sub-product';

    return [
      ...(p.isRefrigerated
        ? [
            {
              label: ar ? 'صنف ثلاجة' : 'Refrigerator Product',
              value: '',
            },
          ]
        : []),
      {
        label: ar ? 'اسم الصنف باللغة العربية' : 'Title (Arabic)',
        value: p?.titleAr,
        fallback: ar ? 'غير متوفر' : 'Not Provided',
      },
      {
        label: ar ? 'اسم الصنف باللغة الإنجليزية' : 'Title (English)',
        value: p?.titleEn,
        fallback: ar ? 'غير متوفر' : 'Not Provided',
      },
      {
        label: ar ? 'الشركة المصنعة' : 'Manufacturing Company',
        value: p.mfgCompanyTitle,
        fallback: ar ? 'غير متوفر' : 'Not Provided',
      },

      ...(showPricing
        ? [
            {
              label: ar ? 'الفئة' : 'Category',
              value: p.categoryTitle,
              fallback: ar ? 'غير متوفر' : 'Not provided',
            },
            {
              label: ar ? 'فئة فرعية' : 'Sub Category',
              value: p.subCategoryTitle,
              fallback: ar ? 'غير متوفر' : 'Not provided',
            },
            {
              label: ar ? 'الزون' : 'Zone',
              value: p.zoneTitle,
              fallback: ar ? 'غير متوفر' : 'Not Provided',
            },
            {
              label: ar ? 'إجمالي الكمية \ الكمية المتاحة' : 'Total Quantity \ Available Quantity',
              value:
                p.totalQuantity !== undefined &&
                p.totalQuantity !== null &&
                p.availableQuantity !== undefined &&
                p.availableQuantity !== null
                  ? `${p.availableQuantity}\\${p.totalQuantity}`
                  : null,
              fallback: ar ? 'غير متوفر بالمخزون' : 'Out of stock',
              valueClass: 'text-emerald-500 font-semibold',
            },
            {
              label: ar ? 'الباركود' : 'Barcode',
              value: p.barcode,
              fallback: ar ? 'غير متوفر' : 'Not provided',
              valueClass: 'text-[14px] font-semibold',
            },
            {
              label: ar ? 'سعر الجمهور' : 'People Price',
              value: `${p.customerPrice || 0}`,
              unit: ar ? 'ج.م' : 'EGP',
              valueClass: 'font-extrabold',
            },
            {
              type: 'price-with-discount' as const,
              label: ar ? 'سعر التاجر' : 'Seller Price',
              value: p.sellerPrice || 0,
              unit: ar ? 'ج.م' : 'EGP',
              secondValue: p.sellerPriceDiscountPercentage ? `%${p.sellerPriceDiscountPercentage}` : null,
            },
            {
              type: 'price-with-discount' as const,
              label: ar ? 'سعر الصيدلي' : 'Pharmacy Price',
              value: p.businessPrice || 0,
              unit: ar ? 'ج.م' : 'EGP',
              secondValue: p.businessPriceDiscountPercentage ? `%${p.businessPriceDiscountPercentage}` : null,
            },
          ]
        : []),

      {
        type: 'description' as const,
        label: ar ? 'الوصف باللغة العربية' : 'Description (Arabic)',
        value: p.descriptionAr,
        fallback: ar ? 'غير متوفر' : 'Not provided',
      },
      {
        type: 'description' as const,
        label: ar ? 'الوصف باللغة الإنجليزية' : 'Description (English)',
        value: p.descriptionEn,
        fallback: ar ? 'غير متوفر' : 'Not provided',
      },
    ];
  });

  formatPrice(price: number | undefined): string {
    if (price === undefined || price === null) return '0.00';
    return price.toLocaleString('en-US', { minimumFractionDigits: 2 });
  }

  get isEnglish() {
    return this.locale.startsWith('en');
  }
}
